import { FormGroup, Label } from "reactstrap";
import __ from "src/utils/Translations";
import React, { FC, useCallback, useMemo, useState } from "react";
import Select from "react-select";
import { debounce } from "lodash";
import { AxiosError } from "axios";
import useFetchPotentialSupervisors from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchPotentialSupervisors";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";

interface ISupervisors {
  companyId: string;
  employeeId: string;
  structureName: string;
  value: any;
  onChange: (value: any) => void;
  isEditable: boolean;
  isDisabled: boolean;
}

interface Supervisor {
  employeeId: string;
  name: string;
  fk: string;
  canBeSupervisor: boolean;
}

const SupervisorsField: FC<ISupervisors> = ({
  companyId,
  employeeId,
  structureName,
  value,
  onChange,
  isEditable,
  isDisabled,
}) => {
  const logStatus = useLogStatus();
  const [inputValue, setInputValue] = useState<string>("");
  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    error: supervisorsError,
  } = useFetchPotentialSupervisors({
    companyId,
    employeeId,
    query: inputValue,
    enabled: Boolean(companyId && employeeId),
  });

  if (supervisorsError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "SupervisorsField: fetchPotentialSupervisors failed",
      statusDetails: {
        source: "API",
        operation: "fetchPotentialSupervisors",
        status: (supervisorsError as AxiosError)?.response?.status,
        url: (supervisorsError as AxiosError)?.config?.url,
      },
    });
  }

  const fetchedSupervisors = useMemo(() => {
    const supervisors =
      data?.pages.flatMap((page) =>
        page.content.map((sup: Supervisor) => ({
          value: sup.employeeId,
          label: `${sup.name} (FK: ${sup.fk}) ${
            !sup.canBeSupervisor ? " - Podwładny pracownika" : ""
          }`,
          isDisabled: !sup.canBeSupervisor,
        }))
      ) || [];

    if (structureName) {
      return [
        {
          value: "",
          label: structureName,
        },
        ...supervisors,
      ];
    }

    return supervisors;
  }, [data, structureName]);

  const handleInputChange = useCallback(
    debounce((value) => {
      setInputValue(value);
    }, 1000),
    []
  );

  const handleScrollToBottom = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      <FormGroup>
        <Label for="supervisor">{__("Przełożony")}</Label>
        <Select
          id="supervisor"
          value={value}
          onChange={onChange}
          menuPlacement="auto"
          options={fetchedSupervisors}
          onInputChange={handleInputChange}
          onMenuScrollToBottom={handleScrollToBottom}
          placeholder={__("Wybierz")}
          noOptionsMessage={() => {
            if (isFetching) return __("Ładowanie...");
            return data?.pages?.every((p) => p.content.length === 0)
              ? __("Brak wyników wyszukiwania")
              : null;
          }}
          loadingMessage={() => __("Ładowanie...")}
          isLoading={isFetching}
          isClearable
          isDisabled={!isEditable || isDisabled}
          isOptionDisabled={(option) => option.isDisabled === true}
        />
      </FormGroup>
    </>
  );
};

export default SupervisorsField;
